import { ChangeEventHandler, ReactNode, useId, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { FileTypes, getAcceptAttributeValue } from "@/utils/mimetypes";

export type FileDropZoneStyle = "blur" | "stroke" | "none";

type FileDropZoneProps = {
  disabled?: boolean;
  className?: string;
  style?: FileDropZoneStyle;
  enableClick?: boolean;
  onClick?: () => void;
  onFiles?: (files: FileList) => void;
  acceptedFileTypes?: FileTypes[];
  multiple?: boolean;
  dragOverlay?: ReactNode;
  overlayClassName?: string;
  children: ReactNode;
};

const baseBg =
  "url(\"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%23A9ACB3FF' stroke-width='1' stroke-dasharray='12%2c 12' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e\")";

const hoverBg =
  "url(\"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%23312453FF' stroke-width='1' stroke-dasharray='12%2c 12' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e\")";

export const FileDropZone = ({
  disabled,
  className,
  style = "blur",
  enableClick = false,
  onClick,
  onFiles,
  acceptedFileTypes,
  multiple = false,
  dragOverlay,
  overlayClassName,
  children,
}: FileDropZoneProps) => {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (!isDragActive) return;
    event.preventDefault();
    if (disabled) return;
    setIsDragActive(false);
    onFiles?.(event.dataTransfer.files);
  };

  const hasFiles = (event: React.DragEvent<HTMLDivElement>) =>
    event.dataTransfer.types.includes("Files");

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!hasFiles(event)) return;
    event.preventDefault();
    if (!disabled) setIsDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!hasFiles(event)) return;
    event.preventDefault();
    if (!disabled) setIsDragActive(false);
  };

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    if (disabled) return;
    if (event.target.files) onFiles?.(event.target.files);
    if (inputRef.current) inputRef.current.value = "";
  };

  const isStroke = style === "stroke";
  const showOverlay = Boolean(dragOverlay) && isDragActive;
  const canClick = Boolean(enableClick && !disabled);

  return (
    <>
      <div
        className={cn(
          "file-drop-zone relative transition-opacity flex flex-col min-h-0",
          isStroke && "file-drop-zone-stroke",
          disabled
            ? "opacity-50 pointer-events-none"
            : canClick || onClick
              ? "cursor-pointer"
              : "cursor-default",
          className
        )}
        onClick={() => {
          if (disabled) return;
          onClick?.();
          if (enableClick) {
            inputRef.current?.click();
          }
        }}
        onDragOver={disabled ? undefined : handleDragOver}
        onDragEnter={disabled ? undefined : handleDragOver}
        onDragLeave={disabled ? undefined : handleDragLeave}
        onDrop={disabled ? undefined : handleDrop}
        style={{
          backgroundImage: isStroke ? baseBg : undefined,
          backgroundPosition: "center",
        }}
      >
        {style === "blur" && (
          <div className="absolute inset-0 bg-background/40 backdrop-blur-sm pointer-events-none" />
        )}
        <div className="relative flex flex-col min-h-0 flex-1">{children}</div>
        {showOverlay && (
          <div
            className={cn(
              "absolute inset-0 z-20 flex items-center justify-center pointer-events-none",
              "bg-background/20 backdrop-blur-[2px]",
              overlayClassName
            )}
          >
            {dragOverlay}
          </div>
        )}
      </div>

      <input
        id={inputId}
        ref={inputRef}
        type="file"
        className="sr-only"
        disabled={disabled}
        onChange={handleInputChange}
        multiple={multiple}
        accept={getAcceptAttributeValue(acceptedFileTypes as FileTypes[])}
        tabIndex={-1}
      />

      <style>
        {`
        .file-drop-zone-stroke:hover {
          background-image: ${hoverBg} !important;
        }
      `}
      </style>
    </>
  );
};
